import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {publicInstance} from '../axiosInstance';
import posthog from 'posthog-js';

interface AddDeviceFormProps {
    open: boolean;
    onClose: () => void;
    onDevicesAdded: () => void;
}

const AddDeviceForm: React.FC<AddDeviceFormProps> = ({ open, onClose, onDevicesAdded }) => {
    const [deviceInput, setDeviceInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const handleAddDevices = async () => {
        const deviceList = deviceInput.split(/[\s,]+/).filter(n => n).map(Number);
        if (deviceList.length === 0) return;

        setLoading(true);
        try {
            await publicInstance.patch(
                `/dacapi_sensorbox_introduction_form?box=in.(${deviceList.join(',')})`,
                { status: "BIURO-TEST" }
            );

            setSnackbar({
                open: true,
                message: 'Devices added successfully',
                severity: 'success'
            });

            setDeviceInput('');
            onDevicesAdded(); // This will trigger the refetch of /view_dacapi_inventory_all

            posthog.capture('devices_added_to_inventory', {
                username: localStorage.getItem('username'),
                device_count: deviceList.length
            });
        } catch (error) {
            console.error('Error adding devices:', error);
            setSnackbar({
                open: true,
                message: 'Error adding devices',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Devices to Test</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={4}
                        value={deviceInput}
                        onChange={(e) => setDeviceInput(e.target.value)}
                        placeholder="Enter device numbers (separated by spaces or commas)..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddDevices}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddDeviceForm;